import React, { useCallback } from "react";
import { motion } from "framer-motion";
import { CorsaWordmark } from "../components/corsa/CorsaWordmark";
import { SlashDivider } from "../components/corsa/SlashDivider";
import { VimeoFrame } from "../components/corsa/VimeoFrame";
import { HubSpotForm } from "../components/corsa/HubSpotForm";
import { LogoStrip } from "../components/corsa/LogoStrip";
import { StickyMobileCTA } from "../components/corsa/StickyMobileCTA";
import { SectionHeading } from "../components/corsa/SectionHeading";
import { FounderVideo } from "../components/corsa/FounderVideo";
import { GuaranteeSteps } from "../components/corsa/GuaranteeSteps";
import { TID } from "../constants/testIds";

const CLIENT_LOGOS = [
  { src: "/assets/logos/collectingcars_logo.png", alt: "Collecting Cars" },
  { src: "/assets/logos/oup-combined-logo-white-transparent.png", alt: "OUP" },
  { src: "/assets/logos/masseuse-massage-chairs-logo---white.png", alt: "Masseuse Massage Chairs" },
  { src: "/assets/logos/id4me-new-logo-a4-landscape-1.png", alt: "iD4me" },
  { src: "/assets/logos/burly-gin-loop-logo.png", alt: "Burly Gin" },
  { src: "/assets/logos/grafico_logo.png", alt: "Grafico" },
  { src: "/assets/logos/auscarts-logo.png", alt: "Auscarts" },
  { src: "/assets/logos/escapexperience-logo.png", alt: "EscapeXperience" },
  { src: "/assets/logos/sunday-retreats.png", alt: "Sunday Retreats" },
  { src: "/assets/logos/ap-pscyhology.png", alt: "AP Psychology" },
  { src: "/assets/logos/unltd.png", alt: "UNLTD" },
];

const T1_SRC = "https://player.vimeo.com/video/1135639958?h=db9333cb08";
const T2_SRC = "https://player.vimeo.com/video/1134117041?h=645d9fcbce";
const T1_POSTER = "/assets/images/testimonial-collecting-cars.png";
const T2_POSTER = "/assets/images/testimonial-rembrandt.png";

const FORM_HEADLINE = "Start the 90-day clock.";
const FORM_SUB =
  "Tell us about your business. We'll come back with the target, the plan, and the guarantee in writing.";

export default function Landing() {
  const focusForm = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => {
        const input = el.querySelector("input, select, textarea");
        if (input) input.focus({ preventScroll: true });
      }, 700);
    }
  }, []);

  const scrollToForm = useCallback(() => focusForm("audit-form"), [focusForm]);

  return (
    <main className="relative" data-testid="landing-root">
      {/* ============================================================
          SECTION 1 — HERO
         ============================================================ */}
      <section
        id="hero"
        data-testid={TID.heroSection}
        className="relative grain"
        style={{ background: "var(--dusk)", color: "var(--cream)" }}
      >
        {/* Top bar — logo + tag */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 pt-6 md:pt-8 flex items-center justify-between">
          <a href="#hero" data-testid={TID.heroLogo} aria-label="CORSA home">
            <CorsaWordmark size="md" />
          </a>
          <div className="hidden md:flex items-center gap-3 text-xs uppercase font-semibold" style={{ color: "var(--milano)" }}>
            <span className="dot-marker" />
            <span>Creating Forward Motion</span>
          </div>
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 pt-10 md:pt-14 pb-20 md:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left col 7 — copy */}
            <div className="lg:col-span-7">
              <div
                className="inline-flex items-center gap-2 mb-7 font-semibold uppercase text-xs md:text-sm"
                style={{ color: "var(--calypso)" }}
                data-testid={TID.heroEyebrow}
              >
                <span className="dot-marker" />
                <span>The Corsa Performance Guarantee</span>
              </div>

              <h1
                data-testid={TID.heroHeadline}
                className="kuunari-italic mb-10 md:mb-14"
                style={{ color: "var(--cream)" }}
              >
                {["Hit your target in 90 days.", "Or your fees back."].map((line, lineIdx) => (
                  <span
                    key={lineIdx}
                    className="block text-[clamp(3rem,7.5vw,7.5rem)]"
                    style={{
                      lineHeight: 0.94,
                      color: lineIdx === 1 ? "var(--calypso)" : "var(--cream)",
                    }}
                  >
                    {line.split(" ").map((w, i) => (
                      <motion.span
                        key={i}
                        initial={{ y: 22, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{
                          duration: 0.55,
                          ease: [0.22, 1, 0.36, 1],
                          delay: lineIdx * 0.22 + i * 0.045,
                        }}
                        className="inline-block mr-[0.2em]"
                      >
                        {w}
                      </motion.span>
                    ))}
                  </span>
                ))}
              </h1>

              <motion.p
                data-testid={TID.heroSubline}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85, duration: 0.6 }}
                className="mt-2 text-base md:text-lg max-w-xl"
                style={{ color: "var(--cream)", opacity: 0.92 }}
              >
                We set one measurable performance target with you on day one. Standard fees during the engagement. If we don&apos;t hit the target inside 90 days, we refund every cent of our fees. Ad spend remains your investment, our work doesn&apos;t.
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.05, duration: 0.5 }}
                className="mt-7 flex flex-wrap items-center gap-4"
              >
                <button
                  type="button"
                  onClick={scrollToForm}
                  className="btn-calypso"
                  data-testid={TID.heroCta}
                >
                  Book My Audit <span className="arrow">→</span>
                </button>
                <div
                  className="flex items-center gap-3 text-sm"
                  style={{ color: "var(--milano)" }}
                  data-testid="hero-proof-line"
                >
                  <span className="dot-marker" />
                  <span className="uppercase font-semibold">
                    18 years. 100+ brands. One guarantee.
                  </span>
                </div>
              </motion.div>
            </div>

            {/* Right col 5 — founder video, bigger now */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="mx-auto"
                style={{ maxWidth: 460 }}
                data-testid={TID.showreelInkReveal}
              >
                <FounderVideo
                  dataTestId="founder-video"
                  poster="/assets/images/vsl-thumbnail.png"
                />
              </motion.div>
            </div>
          </div>

          {/* Trusted by — full width across the bottom of the hero */}
          <div
            className="mt-14 md:mt-20"
            data-testid="hero-trusted-by"
          >
            <div
              className="mb-4 flex items-center gap-3 text-xs uppercase font-semibold"
              style={{ color: "var(--milano)" }}
            >
              <span className="dot-marker" />
              <span>Trusted by</span>
            </div>
            <LogoStrip logos={CLIENT_LOGOS} />
          </div>
        </div>

        {/* Forward / Slash transition bottom */}
        <SlashDivider
          from="#004DBF"
          stripes={["#FFAE00", "#F95300", "#00563E"]}
          to="#FBF6E9"
          height={28}
        />
      </section>

      {/* ============================================================
          SECTION 2 — HOW THE GUARANTEE WORKS
         ============================================================ */}
      <GuaranteeSteps />

      <SlashDivider
        from="#FBF6E9"
        stripes={["#FFAE00", "#F95300", "#00563E"]}
        to="#004DBF"
        height={28}
        fullWidth
      />

      {/* ============================================================
          SECTION 3 — DEDICATED FORM SECTION
         ============================================================ */}
      <section
        id="audit-form-section"
        data-testid="form-section"
        className="relative grain"
        style={{ background: "var(--dusk)", color: "var(--cream)" }}
      >
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-6">
              <div
                className="inline-flex items-center gap-2 mb-5 font-semibold uppercase text-xs md:text-sm"
                style={{ color: "var(--calypso)" }}
              >
                <span className="dot-marker" />
                <span>Free audit / 90-day guarantee</span>
              </div>
              <h2
                className="kuunari-italic"
                style={{ color: "var(--cream)" }}
                data-testid="form-section-headline"
              >
                <span className="block text-[clamp(2.6rem,6vw,5.4rem)]" style={{ lineHeight: 0.94 }}>
                  {"Start the 90-Day Clock.".split(" ").map((w, i) => (
                    <motion.span
                      key={i}
                      initial={{ y: 18, opacity: 0 }}
                      whileInView={{ y: 0, opacity: 1 }}
                      viewport={{ once: true, amount: 0.4 }}
                      transition={{ duration: 0.55, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                      className="inline-block mr-[0.2em]"
                      style={i === 3 ? { color: "var(--calypso)" } : undefined}
                    >
                      {w}
                    </motion.span>
                  ))}
                </span>
              </h2>
              <p
                className="mt-7 text-base md:text-lg max-w-lg"
                style={{ color: "var(--milano)", lineHeight: 1.55 }}
                data-testid="form-section-sub"
              >
                {FORM_SUB}
              </p>
              <div
                className="mt-8 flex items-center gap-3 text-sm"
                style={{ color: "var(--cream)", opacity: 0.92 }}
              >
                <span className="dot-marker" />
                <span className="uppercase font-semibold">
                  18 years. 100+ brands. One guarantee.
                </span>
              </div>
            </div>
            <div className="lg:col-span-6">
              <HubSpotForm containerId="audit-form" />
            </div>
          </div>
        </div>

        <SlashDivider
          from="#004DBF"
          stripes={["#FFAE00", "#F95300", "#00563E"]}
          to="#FBF6E9"
          height={28}
        />
      </section>

      {/* ============================================================
          SECTION 4 — TESTIMONIALS
         ============================================================ */}
      <section
        data-testid={TID.testimonialsSection}
        id="testimonials"
        className="relative grain"
        style={{ background: "var(--cream)", color: "var(--track)" }}
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 md:mb-16">
            <SectionHeading
              eyebrow="Testimonials"
              text="Two industries. Two targets. The Guarantee held both times."
              color="var(--track)"
              eyebrowColor="var(--nineeleven)"
              size="md"
            />
            <div className="max-w-md text-sm md:text-base" style={{ color: "var(--track)", opacity: 0.7 }}>
              Two clients. Two industries. One pattern. More leads at a lower cost, hit inside 90 days.
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-10">
            <TestimonialCard
              testId={TID.testimonialCollectingCars}
              src={T1_SRC}
              poster={T1_POSTER}
              name="Lee Hallett"
              role="Head of APAC"
              company="Collecting Cars"
              stripeColors={["var(--calypso)", "var(--nineeleven)", "var(--dusk)", "var(--racer)"]}
            />
            <TestimonialCard
              testId={TID.testimonialRembrandt}
              src={T2_SRC}
              poster={T2_POSTER}
              name="Josh Spasevski"
              role="Founder"
              company="Rembrandt Caravans"
              stripeColors={["var(--nineeleven)", "var(--calypso)", "var(--racer)", "var(--dusk)"]}
            />
          </div>

          <div className="mt-14 md:mt-20 flex flex-col items-center text-center">
            <div className="kuunari-italic text-2xl md:text-3xl mb-5" style={{ color: "var(--track)" }} data-testid="mid-page-cta">
              Same pattern, your business. Book the audit.
            </div>
            <button
              type="button"
              onClick={scrollToForm}
              className="btn-calypso"
              data-testid="testimonials-cta-button"
            >
              Book My Audit <span className="arrow">→</span>
            </button>
          </div>
        </div>

        <SlashDivider
          from="#FBF6E9"
          stripes={["#FFAE00", "#F95300", "#00563E"]}
          to="#004DBF"
          height={28}
        />
      </section>

      {/* ============================================================
          SECTION 5 — FINAL CTA + FOOTER
         ============================================================ */}
      <section
        data-testid={TID.finalCtaSection}
        id="final-cta"
        className="relative grain overflow-hidden"
        style={{ background: "var(--dusk)", color: "var(--cream)" }}
      >
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36 text-center">
          <div
            className="inline-flex items-center gap-2 mb-6 font-semibold uppercase text-xs md:text-sm"
            style={{ color: "var(--calypso)" }}
          >
            <span className="dot-marker" />
            <span>One last thing</span>
          </div>
          <h2 className="kuunari-italic" style={{ color: "#FFFFFF" }}>
            <span className="block text-[clamp(2.4rem,6.6vw,6rem)]" style={{ lineHeight: 0.92 }}>
              {"Stop guessing where your ad spend is going".split(" ").map((w, i) => (
                <motion.span
                  key={i}
                  initial={{ y: 24, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.55, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-block mr-[0.18em]"
                >
                  {w}
                </motion.span>
              ))}
            </span>
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-base md:text-lg" style={{ color: "var(--milano)" }}>
            Set a target with us, hit it in 90 days, or you don&apos;t pay. Book your free audit.
          </p>
          <p
            className="mt-3 max-w-2xl mx-auto text-sm md:text-base font-semibold"
            style={{ color: "var(--calypso)" }}
            data-testid="final-cta-guarantee-line"
          >
            Backed by the Corsa Performance Guarantee.
          </p>
          <div className="mt-10">
            <button
              type="button"
              onClick={scrollToForm}
              className="btn-calypso"
              data-testid={TID.finalCtaButton}
            >
              Book My Audit <span className="arrow">→</span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <footer
          data-testid={TID.footer}
          className="relative border-t grain"
          style={{ background: "var(--racer)", color: "var(--cream)", borderColor: "rgba(251,246,233,0.15)" }}
        >
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
              <div>
                <div className="kuunari-italic text-3xl md:text-5xl mb-3" style={{ color: "var(--cream)" }}>
                  We are Corsa.
                </div>
                <div className="kuunari-italic text-2xl md:text-4xl" style={{ color: "var(--calypso)" }}>
                  Built to create forward motion.
                </div>
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm uppercase font-semibold" style={{ color: "var(--milano)" }}>
                <span>Strategy</span>
                <span style={{ color: "var(--calypso)" }}>/</span>
                <span>Creative</span>
                <span style={{ color: "var(--calypso)" }}>/</span>
                <span>Amplification</span>
              </div>
            </div>

            <div className="my-10 racing-stripe" aria-hidden="true">
              <span style={{ background: "var(--calypso)" }} />
              <span style={{ background: "var(--nineeleven)" }} />
              <span style={{ background: "var(--calypso)" }} />
              <span style={{ background: "var(--milano)" }} />
            </div>

            <div className="grid md:grid-cols-3 gap-8 md:gap-10">
              <div>
                <CorsaWordmark size="sm" />
                <p
                  className="mt-3 text-sm"
                  style={{ color: "var(--milano)", opacity: 0.9 }}
                  data-testid="footer-tagline"
                >
                  We put our fees on the line, not yours.
                </p>
              </div>
              <div>
                <div className="text-xs uppercase font-semibold mb-2" style={{ color: "var(--calypso)" }}>QLD office</div>
                <address className="not-italic text-sm" style={{ color: "var(--cream)", opacity: 0.9 }}>
                  36 Warry Street<br />Fortitude Valley<br />Brisbane, QLD
                </address>
              </div>
              <div>
                <div className="text-xs uppercase font-semibold mb-2" style={{ color: "var(--calypso)" }}>VIC office</div>
                <address className="not-italic text-sm" style={{ color: "var(--cream)", opacity: 0.9 }}>
                  68 Clarke Street<br />Southbank<br />Melbourne, VIC
                </address>
              </div>
            </div>

            {/* Guarantee small print */}
            <p
              className="mt-10 text-xs leading-relaxed max-w-3xl"
              style={{ color: "var(--milano)", opacity: 0.75 }}
              data-testid="guarantee-terms-line"
            >
              The Corsa Performance Guarantee applies where clients implement Corsa&apos;s recommendations. Required media spend to achieve the agreed target is covered by the client and is not refundable. The guarantee covers Corsa&apos;s fees only.
            </p>

            <div
              className="mt-8 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs"
              style={{ color: "var(--milano)", opacity: 0.8, borderTop: "1px solid rgba(251,246,233,0.15)" }}
            >
              <div>© 2026 Corsa Media Pty Ltd</div>
              <div style={{ color: "var(--milano)" }}>Creating Forward Motion</div>
            </div>
          </div>
        </footer>
      </section>

      <StickyMobileCTA targetId="audit-form" onClick={scrollToForm} />
    </main>
  );
}

const TestimonialCard = ({ testId, src, poster, name, role, company, stripeColors }) => {
  return (
    <motion.figure
      data-testid={testId}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col"
    >
      <div
        className="relative w-full mx-auto"
        style={{
          maxWidth: 420,
          border: "3px solid var(--track)",
          borderRadius: 15,
          overflow: "hidden",
          boxShadow: "8px 8px 0 0 var(--track)",
          background: "var(--track)",
        }}
      >
        <VimeoFrame
          src={src}
          poster={poster}
          title={`${name}, ${company}`}
          aspect="9/16"
          dataTestId={`${testId}-video`}
          rounded={12}
        />
      </div>

      <figcaption className="mt-5 mx-auto w-full" style={{ maxWidth: 420 }}>
        <div
          className="relative flex items-stretch overflow-hidden"
          style={{
            background: "var(--dusk)",
            color: "var(--cream)",
            borderRadius: 12,
            border: "2px solid var(--track)",
            boxShadow: "4px 4px 0 0 var(--track)",
            minHeight: 96,
          }}
        >
          <div className="flex-1 flex flex-col justify-center px-5 py-4">
            <div
              className="kuunari-italic leading-none mb-1"
              style={{ color: "var(--calypso)", fontSize: "clamp(1.4rem, 3vw, 1.9rem)" }}
            >
              {name}
            </div>
            <div
              className="text-xs md:text-sm uppercase tracking-[0.18em] font-semibold"
              style={{ color: "var(--cream)" }}
            >
              {role} <span style={{ color: "var(--calypso)", margin: "0 0.35em" }}>/</span> {company}
            </div>
          </div>
        </div>
      </figcaption>
    </motion.figure>
  );
};
